import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { ApiError, api, isNetworkError, tokenStore, unwrap } from '../api/client';
import { apiUrl, isCrossOrigin, ssoLoginUrl, ssoLogoutUrl } from '../api/config';
import type { Me, Permission } from '../api/types';
import { Loading } from '../components/ui';
import { LoginPage, NoAccountPage } from './LoginPage';

type AuthContextValue = {
  me: Me;
  can: (permission: Permission) => boolean;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const SSO_KEY = 'ita.ssoAttempt';

/** Höchstens alle 2 Minuten automatisch weiterleiten – verhindert Schleifen, wenn Access (noch) nicht eingerichtet ist. */
const startSsoOnce = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(SSO_KEY) ?? 0);
    if (Date.now() - last < 120_000) return false;
    sessionStorage.setItem(SSO_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.href = ssoLoginUrl();
  return true;
};

/**
 * Läuft vor der API Cloudflare Access? Lokal (ohne Access) antwortet /api/health mit
 * cloudflare_access=false → keine SSO-Weiterleitung, nur Token-Anmeldung.
 * Hinter Access leitet Access den Aufruf zur Anmeldung um → fetch scheitert → SSO.
 */
const checkSso = async (): Promise<boolean> => {
  try {
    const res = await fetch(apiUrl('/api/health'), { credentials: 'omit' });
    const body = (await res.json()) as { auth?: { cloudflare_access?: boolean } };
    return body.auth?.cloudflare_access === true;
  } catch {
    return true;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => unwrap(api.GET('/api/me')),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const err = meQuery.error;
  const notLoggedIn = !!err && !tokenStore.get() && ((err instanceof ApiError && err.status === 401) || isNetworkError(err));
  const sso = useQuery({
    queryKey: ['sso-available'],
    queryFn: checkSso,
    enabled: notLoggedIn && isCrossOrigin,
    retry: false,
    staleTime: Infinity,
  });

  const logout = useCallback(() => {
    const viaAccess = meQuery.data?.auth_method === 'cloudflare_access';
    tokenStore.clear();
    qc.clear();
    if (viaAccess) window.location.href = ssoLogoutUrl();
    else void qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc, meQuery.data?.auth_method]);

  const value = useMemo<AuthContextValue | null>(() => {
    if (!meQuery.data) return null;
    const perms = new Set(meQuery.data.permissions);
    return { me: meQuery.data, can: (p) => perms.has(p), logout };
  }, [meQuery.data, logout]);

  if (meQuery.isPending) return <Loading label="Anmeldung wird geprüft …" />;

  if (err) {
    if (err instanceof ApiError && (err.code === 'USER_NOT_REGISTERED' || err.code === 'USER_INACTIVE')) {
      return <NoAccountPage error={err} onLogout={logout} />;
    }
    // API auf eigener Adresse mit Cloudflare Access: einmal automatisch zur SSO-Anmeldung und zurück.
    if (notLoggedIn && isCrossOrigin && sso.isPending) return <Loading label="Anmeldung wird geprüft …" />;
    const ssoAvailable = isCrossOrigin && sso.data === true;
    if (notLoggedIn && ssoAvailable && startSsoOnce()) return <Loading label="Weiterleitung zur Anmeldung …" />;
    return (
      <LoginPage
        sso={ssoAvailable}
        error={notLoggedIn ? null : err}
        onLogin={(token) => {
          tokenStore.set(token);
          void qc.resetQueries({ queryKey: ['me'] });
        }}
      />
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth außerhalb von AuthProvider');
  return ctx;
};

/** Inhalt nur mit Berechtigung anzeigen. */
export function Can({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : null;
}
